import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { MarineInsuranceManagementModel } from '@data-layer/models/marine-insurance';
import { FormHelper } from '@helpers/form.helper';
import { MarineInsuranceQuoteManagementFormModel } from '../../models';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { TranslateService } from '@ngx-translate/core';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { IDefaultSelect } from '@interfaces/default-select';
import { enumInsuranceShipmentType } from '@data-layer/enums/marine-insurance';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-marine-insurance-shipment-details-sideform',
  templateUrl: './marine-insurance-sidebar-shipment-details-form.component.html',
  styleUrls: ['./marine-insurance-sidebar-shipment-details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceSidebarShipmentDetailsFormComponent implements OnInit, OnDestroy {
  @Input() public marineInsurance: MarineInsuranceManagementModel;
  @Input() public isSaving: boolean;
  @Output() public save: EventEmitter<MarineInsuranceQuoteManagementFormModel> =
    new EventEmitter<MarineInsuranceQuoteManagementFormModel>();

  public formGroup: UntypedFormGroup;
  public insuranceShipmentTypes: IDefaultSelect[];
  public insurancePackingTypes: IDefaultSelect[];
  public defaultErrors: { [name: string]: string } = { required: 'FORM_ERROR.REQUIRED' };

  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private fh: FormHelper, private translateService: TranslateService, private fb: UntypedFormBuilder) {}

  public ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      transportationMode: new UntypedFormControl(null, [Validators.required]),
      insuredValue: this.fb.group({ typeOfShipment: new UntypedFormControl(null, [Validators.required]) }),
      billNumber: this.fb.group({
        carrier: new UntypedFormControl(null, [Validators.required]),
        number: new UntypedFormControl(null, [Validators.required]),
      }),
      originCity: new UntypedFormControl(null, [Validators.required]),
      originCountry: new UntypedFormControl(null, [Validators.required]),
      destinationCountry: new UntypedFormControl(null, [Validators.required]),
      destinationState: new UntypedFormControl(null, [Validators.required]),
      destinationCity: new UntypedFormControl(null, [Validators.required]),
      shippingDate: new UntypedFormControl(null, [Validators.required]),
      vesselName: new UntypedFormControl(null, [Validators.required]),
      voyageNumber: new UntypedFormControl(null, [Validators.required]),
    });

    this.formGroup
      .get('transportationMode')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: enumTransportationMode) => {
        this.formGroup.get(['insuredValue', 'typeOfShipment']).setValidators(null);
        this.formGroup.get('vesselName').setValidators(null);

        this.formGroup.get(['insuredValue', 'typeOfShipment']).reset();
        this.formGroup.get('vesselName').reset();
        this.formGroup.get('voyageNumber').reset();

        if (value === enumTransportationMode.OCEAN) {
          this.formGroup.get(['insuredValue', 'typeOfShipment']).setValidators([Validators.required]);
          this.formGroup.get('vesselName').setValidators([Validators.required]);
        }
      });

    this.insuranceShipmentTypes = Object.keys(enumTransportationMode)
      .filter((item: string) => Number(item) > -1 && Number(item) !== enumTransportationMode.RAIL)
      .map((item: any) => ({
        id: Number(item),
        label: this.translateService.instant('LABELS.' + enumTransportationMode[item]),
        value: Number(item),
      }));

    this.insurancePackingTypes = Object.keys(enumInsuranceShipmentType)
      .filter((item: string) => Number(item) > -1)
      .map((item: any) => ({
        id: Number(item),
        label: this.translateService.instant('MARINE_INSURANCE_SHIPMENT_TYPE.' + enumInsuranceShipmentType[item]),
        value: Number(item),
      }));

    this.patchForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public patchForm(): void {
    if (this.isUpdatedInsurance) {
      this.formGroup.patchValue({
        transportationMode: this.marineInsurance.updatedTransportationMode || this.marineInsurance.transportationMode,
        insuredValue: {
          typeOfShipment: this.marineInsurance.updatedInsuredValue?.typeOfShipment || this.marineInsurance.insuredValue?.typeOfShipment,
        },
        billNumber: {
          carrier: this.marineInsurance.updatedBillNumber?.carrier || this.marineInsurance.billNumber?.carrier,
          number: this.marineInsurance.updatedBillNumber?.number || this.marineInsurance.billNumber?.number,
        },
        originCity: this.marineInsurance.updatedOriginCity || this.marineInsurance.originCity,
        originCountry: this.marineInsurance.updatedOriginCountry || this.marineInsurance.originCountry,
        destinationCity: this.marineInsurance.updatedDestinationCity || this.marineInsurance.destinationCity,
        destinationState: this.marineInsurance.updatedDestinationState || this.marineInsurance.destinationState,
        destinationCountry: this.marineInsurance.updatedDestinationCountry || this.marineInsurance.destinationCountry,
        shippingDate: this.marineInsurance.updatedShippingDate || this.marineInsurance.shippingDate,
        vesselName: this.marineInsurance.updatedVesselName || this.marineInsurance.vesselName,
        voyageNumber: this.marineInsurance.updatedVoyageNumber || this.marineInsurance.voyageNumber,
      });
    } else {
      this.formGroup.patchValue({
        transportationMode: this.marineInsurance.transportationMode,
        insuredValue: { typeOfShipment: this.marineInsurance.insuredValue?.typeOfShipment },
        billNumber: {
          carrier: this.marineInsurance.billNumber?.carrier,
          number: this.marineInsurance.billNumber?.number,
        },
        originCity: this.marineInsurance.originCity,
        originCountry: this.marineInsurance.originCountry,
        destinationCity: this.marineInsurance.destinationCity,
        destinationState: this.marineInsurance.destinationState,
        destinationCountry: this.marineInsurance.destinationCountry,
        shippingDate: this.marineInsurance.shippingDate,
        vesselName: this.marineInsurance.vesselName,
        voyageNumber: this.marineInsurance.voyageNumber,
      });
    }
  }

  public onSave(): void {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.valid) {
      this.isSaving = true;

      const formData: MarineInsuranceManagementModel = this.formGroup.getRawValue();

      this.save.emit({
        updatedTransportationMode: formData?.transportationMode,
        updatedInsuredValue: { ...formData?.insuredValue, typeOfShipment: formData?.insuredValue?.typeOfShipment },
        updatedBillNumber: formData?.billNumber,
        updatedOriginCity: formData?.originCity,
        updatedOriginCountryId: formData?.originCountry?.id,
        updatedDestinationCity: formData?.destinationCity,
        updatedDestinationStateId: formData?.destinationState?.id,
        updatedDestinationCountryId: formData?.destinationCountry?.id,
        updatedShippingDate: formData?.shippingDate,
        updatedVesselName: formData?.vesselName,
        updatedVoyageNumber: formData?.voyageNumber,
      });
    }
  }

  public get isUpdatedInsurance(): boolean {
    return !!this.marineInsurance?.updatedTransportationMode || !!this.marineInsurance?.updatedShippingDate;
  }

  public get isAirTransportation(): boolean {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR;
  }

  public get blCarrierLabel(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR ? 'AWB_PREFIX' : 'LABELS.CARRIER';
  }

  public get blCarrierMask(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR ? '0{3}' : 'S{4}';
  }

  public get blCarrierMaskTooltip(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR
      ? 'MASK_ERROR.ONLY_DIGITS'
      : 'MASK_ERROR.ONLY_ENGLISH';
  }

  public get blCarrierMaxMaskLength(): number {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR ? 3 : 4;
  }

  public get blNumberLabel(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR ? 'AWB_NUMBER' : 'LABELS.BL';
  }

  public get blNumberMaskTooltip(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR
      ? 'MASK_ERROR.ONLY_DIGITS'
      : 'MASK_ERROR.ONLY_ENGLISH_DIGITS';
  }

  public get blNumberMask(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR ? '0{9}' : 'A{12}';
  }

  public get blNumberMaxMaskLength(): number {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR ? 9 : 12;
  }

  public get defaultCountry(): string {
    return USA_COUNTRY_CODE;
  }

  public get voyageNumberLabel(): string {
    return this.formGroup.get('transportationMode').value === enumTransportationMode.AIR
      ? 'FORM_LABEL.FLIGHT_NUMBER'
      : 'LABELS.VOYAGE_NUMBER';
  }
}
